import { useEffect, useRef, useState } from "react";
import { Eraser, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";

const W = 440, H = 170;

export const SignaturePad = ({ onSave, busy }) => {
  const ref = useRef(null);
  const last = useRef(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const c = ref.current, dpr = window.devicePixelRatio || 1;
    c.width = W * dpr; c.height = H * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#2c1428"; ctx.lineWidth = 2.6;
  }, []);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
  };
  const down = (e) => {
    ref.current.setPointerCapture(e.pointerId);
    const p = pos(e);
    last.current = { ...p, mx: p.x, my: p.y };
    const ctx = ref.current.getContext("2d");
    ctx.beginPath(); ctx.arc(p.x, p.y, 1.2, 0, Math.PI * 2); ctx.fillStyle = "#2c1428"; ctx.fill();
    setEmpty(false);
  };
  const move = (e) => {
    if (!last.current) return;
    const p = pos(e), l = last.current, ctx = ref.current.getContext("2d");
    const mx = (l.x + p.x) / 2, my = (l.y + p.y) / 2;
    ctx.beginPath(); ctx.moveTo(l.mx, l.my); ctx.quadraticCurveTo(l.x, l.y, mx, my); ctx.stroke();
    last.current = { ...p, mx, my };
  };
  const up = () => { last.current = null; };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, W, H);
    setEmpty(true);
  };
  const save = () => ref.current.toBlob((b) => onSave(new File([b], "signature.png", { type: "image/png" })), "image/png");

  return (
    <div className="space-y-3">
      <div className="relative rounded-md border border-dashed border-plum-300 bg-white">
        <canvas ref={ref} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} onPointerCancel={up}
          style={{ width: "100%", aspectRatio: `${W} / ${H}`, touchAction: "none" }} className="block cursor-crosshair" data-testid="signature-pad-canvas" />
        <div className="pointer-events-none absolute inset-x-6 bottom-8 border-b border-slate-300" />
        {empty && <div className="pointer-events-none absolute inset-0 grid place-items-center text-sm text-slate-400">Sign here with your finger or mouse</div>}
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={clear} disabled={empty || busy} data-testid="signature-pad-clear-btn"><Eraser className="mr-2 h-4 w-4" />Clear</Button>
        <Button onClick={save} disabled={empty || busy} className="bg-plum-700 hover:bg-plum-800" data-testid="signature-pad-save-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save signature
        </Button>
      </div>
    </div>
  );
};
